import { Trans, useTranslation } from 'react-i18next';
import { ApprovalFlowStepItem } from '@/accessControl/ApprovalFlowStepItem.molecule';
import { Alert, Modal } from '@seeqdev/qomponents';
import type { WorkbookApprovalFlow } from '@/worksheet/worksheet.types';

type ApprovalFlowModalProps = {
  isApplyingFlow?: boolean;
  approvalFlow: WorkbookApprovalFlow | null;
  /** The step the item is waiting to be approved into, highlighted in the list. */
  currentStepId?: string;
  itemType: string;
  isApplying: boolean;
  onSubmit: () => Promise<void>;
  onClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
};

export const ApprovalFlowModal = ({
  isApplyingFlow = false,
  approvalFlow,
  currentStepId,
  itemType,
  isApplying,
  onSubmit,
  onClose,
  onCloseAutoFocus,
}: ApprovalFlowModalProps) => {
  const { t } = useTranslation();
  const steps = approvalFlow?.steps ?? [];
  const lockStep = steps.find((step) => step.lockRequired);
  const finalStep = steps[steps.length - 1];

  return (
    <Modal
      open={true}
      onClose={onClose}
      onCloseAutoFocus={onCloseAutoFocus}
      onSubmit={onSubmit}
      disableSubmitButton={isApplying}
      submitButtonLabel={isApplyingFlow ? t('APPROVAL_FLOWS.APPLY') : t('DONE')}
      cancelButtonLabel={t('CANCEL')}
      testId="applyApprovalFlowModal"
      title={
        isApplyingFlow
          ? t('APPROVAL_FLOWS.APPLY_TITLE')
          : t('APPROVAL_FLOWS.VIEW_TITLE', { flowName: approvalFlow?.name })
      }>
      <div className="tw:flex tw:flex-col tw:gap-4" aria-busy={isApplying}>
        {isApplying ? (
          <span role="status" className="tw:sr-only">
            {t('APPROVAL_FLOWS.APPLYING')}
          </span>
        ) : null}
        {isApplyingFlow ? (
          <div className="tw:flex tw:flex-col tw:gap-4" data-testid="applyApprovalFlowDescription">
            <p>{t('APPROVAL_FLOWS.APPLY_DESCRIPTION', { flowName: approvalFlow?.name, itemType })}</p>
            {lockStep ? (
              <p>{t('APPROVAL_FLOWS.APPLY_LOCK_DESCRIPTION', { lockStep: lockStep.name, itemType })}</p>
            ) : null}
          </div>
        ) : null}
        <div>
          <div id="approval-steps-label" className="tw:mb-2 tw:font-bold tw:text-inherit">
            {t('APPROVAL_FLOWS.STEPS')}
          </div>
          <ol aria-labelledby="approval-steps-label" className="tw:my-1 tw:list-decimal tw:gap-1 tw:pl-5">
            {steps.map((step) => (
              <ApprovalFlowStepItem
                key={step.id}
                step={step}
                isCurrentStep={step.id === currentStepId}
                itemType={itemType}
              />
            ))}
          </ol>
        </div>
        {isApplyingFlow && finalStep ? (
          <Alert
            variant="warning"
            dismissible={false}
            testId="applyApprovalFlowProductionImpact"
            extraClassNames="tw:items-start">
            <span className="tw:w-full">
              <Trans
                i18nKey="APPROVAL_FLOWS.APPLY_FINAL_STEP_PRODUCTION_DESCRIPTION"
                values={{
                  stepName: finalStep.name,
                  itemType,
                }}
                components={{ Status: <em /> }}
              />
            </span>
          </Alert>
        ) : null}
      </div>
    </Modal>
  );
};
